import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Polyline, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Route as RouteIcon } from 'lucide-react';
import MapTouchGate from '../../map/MapTouchGate';
import Button from '../../ui/Button';
import { runLengthKm, suggestStopOrder, type Point } from '../../../utils/stopOrder';
import type { DespatchOrder } from '../../../types/nursery';

/**
 * A run's stops on a map, numbered in their current order, with a suggested
 * order to compare and apply. Straight-line distance only: the suggestion is
 * for a person to look at, not a route to drive.
 *
 * A stop is placed from its delivery address's coordinates, which exist only
 * when the address was picked from the suggestions on the customer page; the
 * rest are listed so somebody can fix them.
 */

export interface RouteMapProps {
  orders: DespatchOrder[];
  farm: Point | null;
  farmName: string;
  editable: boolean;
  busy: boolean;
  onApply: (orderIds: number[]) => void;
}

const numberIcon = (n: number | string, colour: string) => L.divIcon({
  className: '',
  html: `<div style="background:${colour};color:#fff;border:2px solid #fff;border-radius:9999px;width:26px;height:26px;display:flex;align-items:center;justify-content:center;font:600 12px/1 system-ui,sans-serif;box-shadow:0 1px 3px rgba(0,0,0,.4)">${n}</div>`,
  iconSize: [26, 26],
  iconAnchor: [13, 13],
});

const pointOf = (o: DespatchOrder): Point | null =>
  o.delivery_lat != null && o.delivery_lng != null ? { lat: Number(o.delivery_lat), lng: Number(o.delivery_lng) } : null;

function FitBounds({ points }: { points: Point[] }) {
  const map = useMap();
  // Not animated: an animated zoom that is still running when the page
  // unmounts ends on a torn-down map, and Leaflet throws from its transition
  // handler — an uncaught error for anyone who navigates away quickly.
  useEffect(() => {
    if (points.length === 1) map.setView([points[0].lat, points[0].lng], 12, { animate: false });
    else if (points.length > 1) map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lng] as [number, number])), { padding: [30, 30], animate: false });
  }, [map, points]);
  return null;
}

export function RouteMap({ orders, farm, farmName, editable, busy, onApply }: RouteMapProps) {
  const { t } = useTranslation('nursery', { keyPrefix: 'RouteMap' });
  const [suggested, setSuggested] = useState<number[] | null>(null);
  useEffect(() => { setSuggested(null); }, [orders]);

  const placed = useMemo(() => orders.filter((o) => pointOf(o)), [orders]);
  const unplaced = orders.filter((o) => !pointOf(o));
  const byId = useMemo(() => new Map(orders.map((o) => [o.id, o])), [orders]);
  const shown = suggested ?? orders.map((o) => o.id);
  const shownPoints = shown.map((id) => pointOf(byId.get(id)!)).filter((p): p is Point => !!p);
  const allPoints = useMemo(() => [...(farm ? [farm] : []), ...placed.map((o) => pointOf(o)!)], [farm, placed]);

  const currentKm = runLengthKm(farm, orders.map(pointOf).filter((p): p is Point => !!p));
  const suggestedKm = suggested ? runLengthKm(farm, shownPoints) : null;

  if (!placed.length) {
    return (
      <p className="text-sm text-neutral-600">
        {t('noneOnMap')}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="h-72 sm:h-96 rounded-lg overflow-hidden border border-neutral-200">
        <MapContainer center={[allPoints[0].lat, allPoints[0].lng]} zoom={10} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds points={allPoints} />
          {farm && (
            <Marker position={[farm.lat, farm.lng]} icon={numberIcon('◆', '#15803d')}>
              <Tooltip>{farmName}</Tooltip>
            </Marker>
          )}
          <Polyline
            positions={[...(farm ? [farm] : []), ...shownPoints].map((p) => [p.lat, p.lng] as [number, number])}
            pathOptions={{ color: suggested ? '#2563eb' : '#6b7280', weight: 3, dashArray: suggested ? '6 6' : undefined }}
          />
          {shown.map((id, i) => {
            const o = byId.get(id)!;
            const p = pointOf(o);
            if (!p) return null;
            return (
              <Marker key={id} position={[p.lat, p.lng]} icon={numberIcon(i + 1, suggested ? '#2563eb' : '#374151')}>
                <Tooltip>{t('stopTooltip', { n: i + 1, customer: o.customer_name, order: o.order_number })}</Tooltip>
              </Marker>
            );
          })}
          <MapTouchGate />
        </MapContainer>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-neutral-600">
          {suggested
            ? t('suggestedKm', { suggested: Math.round(suggestedKm!), current: Math.round(currentKm) })
            : t('asOrderedKm', { current: Math.round(currentKm), farm: farmName })}
        </span>
        {editable && placed.length > 1 && (
          suggested ? (
            <span className="flex flex-wrap gap-2 ml-auto">
              <Button variant="outline" size="sm" onClick={() => setSuggested(null)} disabled={busy}>{t('keepMine')}</Button>
              <Button size="sm" onClick={() => onApply(suggested)} disabled={busy}>{t('useThisOrder')}</Button>
            </span>
          ) : (
            <Button variant="outline" size="sm" className="ml-auto" disabled={busy}
              onClick={() => setSuggested(suggestStopOrder(farm, orders.map((o) => ({ id: o.id, ...(pointOf(o) ?? { lat: null, lng: null }) }))))}>
              <RouteIcon className="h-4 w-4" /> {t('suggest')}
            </Button>
          )
        )}
      </div>

      {unplaced.length > 0 && (
        <p className="text-xs text-amber-800 break-words">
          {t('notOnMap', { orders: unplaced.map((o) => o.order_number).join(', ') })}
        </p>
      )}
    </div>
  );
}

export default RouteMap;
